import Service, { service } from '@ember/service';
import {
  bucketDashboardPoints,
  type DashboardAggregation,
  type DashboardBucket,
} from 'admin-panel/utils/dashboard-buckets';

import type StoreService from '@ember-data/store';
import type CategoryModel from 'admin-panel/models/category';
import type HotelModel from 'admin-panel/models/hotel';
import type ItemModel from 'admin-panel/models/item';
import type PageModel from 'admin-panel/models/page';
import type KeenService from 'admin-panel/services/keen';
import type {
  KeenAnalysis,
  KeenFilter,
  KeenGroupedRow,
  KeenIntervalRow,
} from 'admin-panel/services/keen';
import type {
  CommerceDashboardMetric,
  DashboardChartMode,
  UsageDashboardMetric,
} from 'admin-panel/types/dashboard';
import type { DashboardTimeframe } from 'admin-panel/utils/dashboard-timeframe';

export interface DashboardListRow {
  id: string;
  value: number;
  title?: string;
  categoryId?: string;
  average?: number;
}

interface ChartQuery {
  collection: string;
  filters: KeenFilter[];
  value: { analysis: KeenAnalysis; targetProperty?: string };
  countFilters?: KeenFilter[];
  aggregation: DashboardAggregation;
}

interface LoadOptions {
  categoryId?: string;
  signal?: AbortSignal;
}

interface KeenOrderItem {
  item_id?: number | string | null;
  total_value?: number | null;
}

const MAX_SESSION_DURATION_SECONDS = 3600;
const CATEGORY_PAGE_SIZE = 100;

export default class DashboardAnalyticsService extends Service {
  @service declare keen: KeenService;
  @service declare store: StoreService;

  async loadUsageTotals(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<Record<UsageDashboardMetric, number>> {
    const filters = usageFilters(hotel);
    const [pageViews, sessions, avgSessionDuration] = await Promise.all([
      this.value('count', 'page_views', filters, timeframe, signal),
      this.value('count', 'sessions', filters, timeframe, signal),
      this.value(
        'average',
        'sessions',
        [...filters, sessionDurationFilter()],
        timeframe,
        signal,
        'duration'
      ),
    ]);

    return { pageViews, sessions, avgSessionDuration };
  }

  loadUsageChart(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: UsageDashboardMetric,
    mode: DashboardChartMode,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const filters = withCategory(usageFilters(hotel), categoryId);
    const sessionFilters = [...filters, sessionDurationFilter()];
    const query: ChartQuery =
      metric === 'avgSessionDuration'
        ? {
            collection: 'sessions',
            filters: sessionFilters,
            value: { analysis: 'sum', targetProperty: 'duration' },
            countFilters: sessionFilters,
            aggregation: 'ratio',
          }
        : {
            collection: metric === 'pageViews' ? 'page_views' : 'sessions',
            filters,
            value: { analysis: 'count' },
            aggregation: 'sum',
          };

    return this.chart(query, timeframe, mode, signal);
  }

  async loadPageViewsByFolder(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ) {
    return this.grouped(
      'count',
      'page_views',
      usageFilters(hotel),
      'category_id',
      timeframe,
      signal
    );
  }

  async loadPageViewsByPage(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const rows = await this.grouped(
      'count',
      'page_views',
      withCategory(usageFilters(hotel), categoryId),
      'page_id',
      timeframe,
      signal
    );

    return this.withTitles(rows, 'page');
  }

  async loadCommerceTotals(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    { signal }: LoadOptions = {}
  ): Promise<Record<CommerceDashboardMetric, number>> {
    const filters = commerceFilters(hotel);
    const [revenue, orders] = await Promise.all([
      this.value('sum', 'orders', filters, timeframe, signal, 'total'),
      this.value('count', 'orders', filters, timeframe, signal),
    ]);

    return { revenue, orders };
  }

  loadCommerceChart(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    mode: DashboardChartMode,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const filters = withCategory(commerceFilters(hotel), categoryId);
    const showsAverageOrderValue = metric === 'revenue' && mode !== 'totals';

    return this.chart(
      {
        collection: 'orders',
        filters,
        value:
          metric === 'revenue'
            ? { analysis: 'sum', targetProperty: 'total' }
            : { analysis: 'count' },
        countFilters: showsAverageOrderValue
          ? [...filters, pricedOrderFilter()]
          : undefined,
        aggregation: showsAverageOrderValue ? 'ratio' : 'sum',
      },
      timeframe,
      mode,
      signal
    );
  }

  async loadCommerceByFolder(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    { signal }: LoadOptions = {}
  ): Promise<DashboardListRow[]> {
    const filters = commerceFilters(hotel);

    if (metric === 'orders') {
      return this.grouped(
        'count',
        'orders',
        filters,
        'category_id',
        timeframe,
        signal
      );
    }

    const [revenueRows, pricedOrderRows] = await Promise.all([
      this.grouped(
        'sum',
        'orders',
        filters,
        'category_id',
        timeframe,
        signal,
        'total'
      ),
      this.grouped(
        'count',
        'orders',
        [...filters, pricedOrderFilter()],
        'category_id',
        timeframe,
        signal
      ),
    ]);
    const pricedOrders = new Map(
      pricedOrderRows.map(({ id, value }) => [id, value])
    );

    return revenueRows.map((row) => {
      const orders = pricedOrders.get(row.id);

      return orders ? { ...row, average: row.value / orders } : row;
    });
  }

  async loadCommerceByItem(
    hotel: HotelModel,
    timeframe: DashboardTimeframe,
    metric: CommerceDashboardMetric,
    { categoryId, signal }: LoadOptions = {}
  ) {
    const orders = await this.keen.query<{ order_items?: KeenOrderItem[] }[]>(
      'extraction',
      {
        event_collection: 'orders',
        filters: withCategory(commerceFilters(hotel), categoryId),
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        property_names: ['order_items'],
      },
      { signal }
    );
    const totals = new Map<string, number>();

    orders
      .flatMap((order) => order.order_items || [])
      .forEach((orderItem) => {
        if (orderItem.item_id === null || orderItem.item_id === undefined) {
          return;
        }

        const id = String(orderItem.item_id);
        const value =
          metric === 'revenue' ? Number(orderItem.total_value) || 0 : 1;

        totals.set(id, (totals.get(id) || 0) + value);
      });

    const rows = [...totals]
      .filter(([, value]) => value > 0)
      .map(([id, value]) => ({ id, value }))
      .sort((left, right) => right.value - left.value);

    return this.withTitles(rows, 'item');
  }

  async loadCategories(hotel: HotelModel) {
    const hotelIds = [hotel.id, ...hotel.hasMany('hotelMixin').ids()];
    const categories: CategoryModel[] = [];

    for (let offset = 0; ; offset += CATEGORY_PAGE_SIZE) {
      const result = await this.store.query('category', {
        'filter[hotel_id_in]': hotelIds,
        limit: CATEGORY_PAGE_SIZE,
        offset,
      });
      const page = result.toArray() as CategoryModel[];
      const total = Number(
        (result as typeof result & { meta?: { total?: number } }).meta?.total
      );

      categories.push(...page);

      if (
        page.length < CATEGORY_PAGE_SIZE ||
        categories.length >= (Number.isFinite(total) ? total : Infinity)
      ) {
        return categories;
      }
    }
  }

  private async value(
    analysis: KeenAnalysis,
    collection: string,
    filters: KeenFilter[],
    timeframe: DashboardTimeframe,
    signal?: AbortSignal,
    targetProperty?: string
  ) {
    const result = await this.keen.query<number | null>(
      analysis,
      {
        event_collection: collection,
        filters,
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        target_property: targetProperty,
      },
      { signal }
    );

    return result ?? 0;
  }

  private async grouped(
    analysis: KeenAnalysis,
    collection: string,
    filters: KeenFilter[],
    groupBy: string,
    timeframe: DashboardTimeframe,
    signal?: AbortSignal,
    targetProperty?: string
  ): Promise<DashboardListRow[]> {
    const rows = await this.keen.query<KeenGroupedRow[]>(
      analysis,
      {
        event_collection: collection,
        filters,
        group_by: groupBy,
        timeframe: timeframe.keenTimeframe,
        timezone: timeframe.timezone,
        target_property: targetProperty,
      },
      { signal }
    );

    return rows
      .filter((row) => row[groupBy] !== null && row[groupBy] !== undefined)
      .map((row) => ({
        id: String(row[groupBy]),
        value: Number(row['result']) || 0,
      }))
      .filter((row) => row.value > 0)
      .sort((left, right) => right.value - left.value);
  }

  private async chart(
    query: ChartQuery,
    timeframe: DashboardTimeframe,
    mode: DashboardChartMode,
    signal?: AbortSignal
  ): Promise<DashboardBucket[]> {
    const base = {
      event_collection: query.collection,
      filters: query.filters,
      timeframe: timeframe.keenTimeframe,
      timezone: timeframe.timezone,
      interval:
        mode === 'hourly-average'
          ? ('hourly' as const)
          : timeframe.keenInterval,
    };
    const [valueRows, countRows] = await Promise.all([
      this.keen.query<KeenIntervalRow[]>(
        query.value.analysis,
        { ...base, target_property: query.value.targetProperty },
        { signal }
      ),
      query.countFilters
        ? this.keen.query<KeenIntervalRow[]>(
            'count',
            { ...base, filters: query.countFilters },
            { signal }
          )
        : Promise.resolve([]),
    ]);
    const counts = new Map(
      countRows.map((row) => [row.timeframe.start, row.value ?? 0])
    );
    const points = valueRows.map((row) => ({
      at: row.timeframe.start,
      value: row.value ?? 0,
      count: counts.get(row.timeframe.start) ?? 0,
    }));

    return bucketDashboardPoints(points, timeframe, {
      mode,
      aggregation: query.aggregation,
    });
  }

  private async withTitles(
    rows: DashboardListRow[],
    modelName: 'page' | 'item'
  ) {
    const records = await Promise.allSettled(
      rows.map((row) => this.store.findRecord(modelName, row.id))
    );

    return rows.map((row, index) => {
      const record = records[index];

      if (record?.status !== 'fulfilled') {
        return row;
      }

      const model = record.value as PageModel | ItemModel;
      const categoryId = model.belongsTo('category').id();

      return categoryId
        ? { ...row, title: model.title, categoryId }
        : { ...row, title: model.title };
    });
  }
}

function usageFilters(hotel: HotelModel): KeenFilter[] {
  return [
    { property_name: 'hotel_uuid', operator: 'eq', property_value: hotel.uuid },
  ];
}

function commerceFilters(hotel: HotelModel): KeenFilter[] {
  return [
    {
      property_name: 'hotel_id',
      operator: 'eq',
      property_value: Number(hotel.id),
    },
    { property_name: 'test_order', operator: 'eq', property_value: false },
  ];
}

function pricedOrderFilter(): KeenFilter {
  return { property_name: 'total', operator: 'gte', property_value: 0 };
}

function sessionDurationFilter(): KeenFilter {
  return {
    property_name: 'duration',
    operator: 'lte',
    property_value: MAX_SESSION_DURATION_SECONDS,
  };
}

function withCategory(filters: KeenFilter[], categoryId?: string) {
  return categoryId
    ? [
        ...filters,
        {
          property_name: 'category_id',
          operator: 'eq' as const,
          property_value: Number(categoryId),
        },
      ]
    : filters;
}

declare module '@ember/service' {
  interface Registry {
    'dashboard-analytics': DashboardAnalyticsService;
  }
}
